import type { CreateCustomerRequest } from '@src/providers/CustomersContext.types';
import type { Customer } from '@talos/kyoko';

// Length limits inferred from backend customer field definitions.
export const CREATE_CUSTOMER_NAME_MAX_LENGTH = 64;
export const CREATE_CUSTOMER_DISPLAY_NAME_MAX_LENGTH = 256;

export interface CreateCustomerFormValues {
  name: string;
  displayName: string;
  allInTier?: string;
  feeTier?: string;
  symbolGroup?: string;
}

export interface CreateCustomerValidationIndex {
  names: ReadonlySet<string>;
}

/**
 * Builds lookup indexes once for a customer snapshot so field validation does not scan every customer on each edit.
 */
export function createCustomerValidationIndex(customers: readonly Customer[]): CreateCustomerValidationIndex {
  const names = new Set<string>();

  for (const customer of customers) {
    names.add(customer.Name);
  }

  return { names };
}

export function validateCreateCustomerName(
  value: string,
  validationIndex: CreateCustomerValidationIndex | undefined
): string | undefined {
  const name = value.trim();

  if (!name) {
    return 'Name is required.';
  }
  if (name.length > CREATE_CUSTOMER_NAME_MAX_LENGTH) {
    return `Name must be ${CREATE_CUSTOMER_NAME_MAX_LENGTH} characters or fewer.`;
  }
  if (validationIndex?.names.has(name)) {
    return 'A customer with this Name already exists.';
  }

  return undefined;
}

export function validateCreateCustomerDisplayName(value: string): string | undefined {
  const displayName = value.trim();

  if (!displayName) {
    return 'Display Name is required.';
  }
  if (displayName.length > CREATE_CUSTOMER_DISPLAY_NAME_MAX_LENGTH) {
    return `Display Name must be ${CREATE_CUSTOMER_DISPLAY_NAME_MAX_LENGTH} characters or fewer.`;
  }
  return undefined;
}

export function getCreateCustomerRequest(values: CreateCustomerFormValues): CreateCustomerRequest {
  return {
    Name: values.name.trim(),
    DisplayName: values.displayName.trim(),
    ...(values.allInTier ? { AllInTier: values.allInTier } : {}),
    ...(values.feeTier ? { FeeTier: values.feeTier } : {}),
    ...(values.symbolGroup ? { SymbolGroup: values.symbolGroup } : {}),
  };
}
